import * as L from 'leaflet';
import {CanvasPoint} from '@shared/models/structure';
import {CANVAS_SNAP_GRID} from './canvas-tile.constants';
import {CanvasPointMove, CanvasSnapResult} from './canvas-snap-grid.models';

const POSITION_EPSILON = 1e-9;

export class CanvasSnapGrid {
  private readonly step: number;
  private readonly maxCellX: number;
  private readonly maxCellY: number;

  constructor(private readonly width: number, private readonly height: number) {
    this.step = width * CANVAS_SNAP_GRID.RATIO;
    this.maxCellX = Math.round(1 / CANVAS_SNAP_GRID.RATIO);
    this.maxCellY = Math.floor(height / this.step + POSITION_EPSILON);
  }

  resolvePosition(latlng: L.LatLng, snapEnabled: boolean): {X: number; Y: number} {
    const lng = Math.min(Math.max(latlng.lng, 0), this.width);
    const lat = Math.min(Math.max(latlng.lat, 0), this.height);
    if (!snapEnabled) {
      return {X: lng / this.width, Y: lat / this.height};
    }
    return this.cellToPosition(this.nearestCell(lng / this.step, 'x'), this.nearestCell(lat / this.step, 'y'));
  }

  snapPoints(points: CanvasPoint[]): CanvasSnapResult {
    const candidates = points.map(point => {
      const cellX = point.X * this.width / this.step;
      const cellY = point.Y * this.height / this.step;
      const x = this.nearestCell(cellX, 'x');
      const y = this.nearestCell(cellY, 'y');
      return {point, cellX, cellY, x, y, offset: Math.hypot(cellX - x, cellY - y)};
    });

    const occupied = new Set<string>();
    const moves: CanvasPointMove[] = [];
    let unsnapped = 0;

    candidates.sort((a, b) => a.offset - b.offset).forEach(({point, cellX, cellY, x, y}) => {
      const cell = this.findFreeCell(x, y, cellX, cellY, occupied);
      if (!cell) {
        unsnapped++;
        return;
      }
      occupied.add(`${cell.x}:${cell.y}`);

      const to: CanvasPoint = {...point, ...this.cellToPosition(cell.x, cell.y)};
      if (Math.abs(to.X - point.X) > POSITION_EPSILON || Math.abs(to.Y - point.Y) > POSITION_EPSILON) {
        moves.push({from: point, to});
      }
    });

    return {moves, unsnapped};
  }

  createLayer(): L.LayerGroup {
    const style: L.PolylineOptions = {
      color: CANVAS_SNAP_GRID.LINE_COLOR,
      weight: CANVAS_SNAP_GRID.LINE_WEIGHT,
      opacity: CANVAS_SNAP_GRID.LINE_OPACITY,
      interactive: false,
    };
    const lines: L.Polyline[] = [];
    for (let cell = 0; cell <= this.maxCellX; cell++) {
      const x = cell === this.maxCellX ? this.width : cell * this.step;
      lines.push(L.polyline([[0, x], [this.height, x]], style));
    }
    for (let cell = 0; cell <= this.maxCellY; cell++) {
      const y = cell * this.step;
      lines.push(L.polyline([[y, 0], [y, this.width]], style));
    }
    return L.layerGroup(lines);
  }

  private nearestCell(value: number, axis: 'x' | 'y'): number {
    const max = axis === 'x' ? this.maxCellX : this.maxCellY;
    return Math.min(Math.max(Math.round(value), 0), max);
  }

  private cellToPosition(cellX: number, cellY: number): {X: number; Y: number} {
    return {X: cellX * CANVAS_SNAP_GRID.RATIO, Y: cellY * this.step / this.height};
  }

  private findFreeCell(nearestX: number, nearestY: number, exactX: number, exactY: number, occupied: Set<string>): {x: number; y: number} | null {
    const reach = CANVAS_SNAP_GRID.MAX_NUDGE_CELLS;
    let best: {x: number; y: number} | null = null;
    let bestDistance = Infinity;

    for (let x = nearestX - reach; x <= nearestX + reach; x++) {
      for (let y = nearestY - reach; y <= nearestY + reach; y++) {
        if (x < 0 || y < 0 || x > this.maxCellX || y > this.maxCellY || occupied.has(`${x}:${y}`)) continue;
        const distance = Math.hypot(x - exactX, y - exactY);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = {x, y};
        }
      }
    }
    return best;
  }
}
